import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, Checkbox, Divider, FormControlLabel, MenuItem, TextField, Theme, ToggleButton, ToggleButtonGroup, Typography,} from '@mui/material';
import {AdvancedDateRangeValue, CommitDateRangeArg, DateRangeBatch, DateRangeCount, DateRangeFormMode, DateRangeInputMode, DateRangeType, PredefinedDateRange,} from './types';
import {DateRangeUtils} from './DateRangeUtils';
import DateUtils, {DateUtilsTypes} from '../../../../core/services/date-utils';
import {DatePicker, DatePickerProps, DateTimePicker, DateTimePickerProps, DateTimePickerSlotsComponentsProps, TimePicker, TimePickerProps, TimePickerSlotsComponentsProps} from '@mui/x-date-pickers';
// @ts-ignore
import {ReactComponent as CheckboxIcon} from '../../../../assets/images/check-box/checkbox.svg';
// @ts-ignore
import {ReactComponent as ActiveCheckboxIcon} from '../../../../assets/images/check-box/checkbox-active.svg';
import {PredefinedDateRanges} from "./PredefinedDateRanges";
import UIUtils from "../../../utils";
import {SxProps} from "@mui/system";
import {DatePickerSlotsComponentsProps} from "@mui/x-date-pickers/DatePicker/DatePicker.types";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DateRangeFormProps {
    /** Current value – used as initial state; changes re-sync via effects */
    value?: CommitDateRangeArg;
    disabled?: boolean;
    /**
     * If provided, locks the form to that mode and hides the mode tabs.
     * If omitted, the user can switch freely between Auto / Advanced / Static.
     */
    mode?: DateRangeFormMode;
    /** Controls HTML input type for Static mode. Defaults to 'date'. */
    inputMode?: DateRangeInputMode;
    /** Auto-mode constraints */
    disallowedTypes?: DateRangeType[];
    disallowedBatches?: DateRangeBatch[];
    disallowUntilNow?: boolean;
    disallowFromNow?: boolean;
    /** Granular auto-mode callbacks (fired on each field change, before Apply) */
    onTypeChanged?: (v?: DateRangeType) => void;
    onBatchChanged?: (v?: DateRangeBatch) => void;
    onCountChanged?: (v?: DateRangeCount) => void;
    onUntilNowChanged?: (v?: boolean) => void;
    onFromNowChanged?: (v?: boolean) => void;
    /** Called when the user confirms the selection (Apply) */
    onCommitted?: (value: CommitDateRangeArg | undefined) => void;
    /** Called when the user cancels (Cancel). Only relevant when hideActions=false */
    onCancelled?: () => void;
    /** Hide the Cancel / Apply action row (useful when embedded inline) */
    hideActions?: boolean;
    /** List of predefined shortcut options */
    predefinedOptions?: PredefinedDateRange[];
    sx?: SxProps<Theme>,
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ALL_TYPES: DateRangeType[] = ['last', 'next', 'current'];
const ALL_BATCHES: DateRangeBatch[] = ['hour', 'day', 'week', 'month', 'quarter', 'year'];

const DateRangeForm: React.FC<DateRangeFormProps> = (props) => {
    const {
        disabled,
        disallowedTypes,
        disallowedBatches,
        disallowUntilNow,
        disallowFromNow,
        onTypeChanged: onTypeChangedProp,
        onBatchChanged: onBatchChangedProp,
        onCountChanged: onCountChangedProp,
        onUntilNowChanged: onUntilNowChangedProp,
        onFromNowChanged: onFromNowChangedProp,
        onCommitted,
        onCancelled,
        hideActions = false,
        inputMode = 'date',
        predefinedOptions,
        sx,
    } = props;

    // ── Mode ─────────────────────────────────────────────────────────────────
    const [mode, setMode] = React.useState<DateRangeFormMode>(props.mode ?? props.value?.mode ?? 'auto');
    const [predefinedId, setPredefinedId] = React.useState<string | undefined>(props.value?.predefinedId);

    /**
     * When the mode changes from the outside (for example, when the parent component applies a predefined option with a specific mode),
     * we need to update the internal mode state to reflect that. This ensures that the correct mode is shown as selected in the UI and
     * that the corresponding form fields are displayed. If the new value doesn't have a mode, we default to 'auto' as a safe fallback,
     * since it is the most flexible option.
     */
    React.useEffect(() => {
        if (props.mode !== undefined) setMode(props.mode);
    }, [props.mode]);

    // ── Auto-mode state ───────────────────────────────────────────────────────
    const initialAuto = props.value?.auto ?? props.value;
    const [type, setType] = React.useState<DateRangeType | undefined>(initialAuto?.type);
    const [batch, setBatch] = React.useState<DateRangeBatch | undefined>(initialAuto?.batch);
    const [count, setCount] = React.useState<DateRangeCount | undefined>(initialAuto?.count);
    const [untilNow, setUntilNow] = React.useState<boolean | undefined>(initialAuto?.untilNow);
    const [fromNow, setFromNow] = React.useState<boolean | undefined>(initialAuto?.fromNow);

    /**
     * When the auto value changes from the outside, we need to update all the related internal states to reflect that.
     */
    React.useEffect(() => {
        setType(props.value?.auto?.type ?? props.value?.type);
    }, [props.value?.auto?.type, props.value?.type]);

    /**
     * When the auto batch changes from the outside, we need to update the internal batch state to reflect that.
     */
    React.useEffect(() => {
        setBatch(props.value?.auto?.batch ?? props.value?.batch);
    }, [props.value?.auto?.batch, props.value?.batch]);

    /**
     * When the auto count changes from the outside, we need to update the internal count state to reflect that.
     */
    React.useEffect(() => {
        setCount(props.value?.auto?.count ?? props.value?.count);
    }, [props.value?.auto?.count, props.value?.count]);

    /**
     * When the auto untilNow changes from the outside, we need to update the internal untilNow state to reflect that.
     */
    React.useEffect(() => {
        setUntilNow(props.value?.auto?.untilNow ?? props.value?.untilNow);
    }, [props.value?.auto?.untilNow, props.value?.untilNow]);

    /**
     * When the auto fromNow changes from the outside, we need to update the internal fromNow state to reflect that.
     */
    React.useEffect(() => {
        setFromNow(props.value?.auto?.fromNow ?? props.value?.fromNow);
    }, [props.value?.auto?.fromNow, props.value?.fromNow]);

    const [startCount, setStartCount] = React.useState<number | undefined>(props.value?.advanced?.startCount);
    const [startBatch, setStartBatch] = React.useState<DateRangeBatch | undefined>(props.value?.advanced?.startBatch);
    const [startType, setStartType] = React.useState<DateRangeType | undefined>(props.value?.advanced?.startType);
    const [startFromNow, setStartFromNow] = React.useState<boolean>(props.value?.advanced?.startFromNow ?? false);

    const [endCount, setEndCount] = React.useState<number | undefined>(props.value?.advanced?.endCount);
    const [endBatch, setEndBatch] = React.useState<DateRangeBatch | undefined>(props.value?.advanced?.endBatch);
    const [endType, setEndType] = React.useState<DateRangeType | undefined>(props.value?.advanced?.endType);
    const [endFromNow, setEndFromNow] = React.useState<boolean>(props.value?.advanced?.endFromNow ?? false);

    /**
     * When the advanced value changes from the outside, we need to update all the related internal states to reflect that. T
     * his ensures that if the parent component controlling this form updates the advanced date range (for example, by applying a predefined option),
     * the form will show the correct values in all the advanced fields. We default to false for the "fromNow" fields if they are not provided in the new value,
     * since it makes more sense for them to be unchecked by default rather than indeterminate.
     */
    React.useEffect(() => {
        const adv = props.value?.advanced;
        setStartCount(adv?.startCount);
        setStartBatch(adv?.startBatch);
        setStartType(adv?.startType);
        setStartFromNow(adv?.startFromNow ?? false);
        setEndCount(adv?.endCount);
        setEndBatch(adv?.endBatch);
        setEndType(adv?.endType);
        setEndFromNow(adv?.endFromNow ?? false);
    }, [props.value?.advanced]);

    // ── Static-mode state ─────────────────────────────────────────────────────

    const [startDate, setStartDate] = React.useState<string>(props.value?.static?.startDate ?? '');
    const [endDate, setEndDate] = React.useState<string>(props.value?.static?.endDate ?? '');

    const startDateDayjs = React.useMemo(() => startDate ? DateUtils.local(startDate) : null, [startDate]);
    const endDateDayjs = React.useMemo(() => endDate ? DateUtils.local(endDate) : null, [endDate]);

    // ── Predefined Logic ─────────────────────────────────────────────────────

    /**
     * Handles the selection of a predefined date range option. When a user clicks on a predefined option,
     * we want to immediately commit that selection without requiring them to click "Apply", since selecting a predefined option
     * is an explicit action that indicates their intent to use that specific range. Before committing,
     * we clear all dynamic states to ensure that the committed value is solely based on the predefined option's ID,
     * and not influenced by any previous manual inputs the user may have made in the form.
     * This also ensures that when a predefined option is selected,the form fields will reset to reflect that choice, and no conflicting values will be present.
     */
    const handleSelectPredefined = React.useCallback((opt: PredefinedDateRange) => {
        // Clear all dynamic states
        setMode('auto');
        setType(undefined);
        setBatch(undefined);
        setCount(undefined);
        setUntilNow(undefined);
        setFromNow(undefined);
        setStartCount(undefined);
        setStartBatch(undefined);
        setStartType(undefined);
        setStartFromNow(false);
        setEndCount(undefined);
        setEndBatch(undefined);
        setEndType(undefined);
        setEndFromNow(false);
        setStartDate('');
        setEndDate('');

        // Commit immediately
        onCommitted?.({
            predefinedId: opt.id
        });
    }, [onCommitted]);

    /**
     * Clears the selected predefined option. This is called whenever the user makes a change to any of the fields in the form,
     * since any manual change would typically invalidate the predefined selection. By clearing the predefinedId, we ensure that the UI no longer shows
     * any predefined option as selected, which accurately reflects that the current selection is now a custom one defined by the user's inputs.
     */
    const clearPredefined = React.useCallback(() => {
        setPredefinedId(undefined);
    }, []);

    // ── Sync internal state when props change ───────────────────────────────

    /**
     * When the predefinedId changes from the outside (for example, when the parent component applies a predefined option),
     * we need to update the internal predefinedId state to reflect that. This ensures that the correct predefined option is shown as selected in the UI.
     * If the new value doesn't have a predefinedId, we set it to undefined, which effectively clears any selected predefined option in the UI.
     */
    React.useEffect(() => {
        setPredefinedId(props.value?.predefinedId);
    }, [props.value?.predefinedId]);

    /**
     * When the static value changes from the outside, we need to update the internal startDate and endDate state to reflect that.
     * This ensures that if the parent component controlling this form updates the static date range (for example, by applying a predefined option), the form will show the correct dates in the inputs. We default to empty strings if the new value doesn't have static dates, which effectively clears the inputs in that case.
     */
    React.useEffect(() => {
        setStartDate(props.value?.static?.startDate ?? '');
        setEndDate(props.value?.static?.endDate ?? '');
    }, [props.value?.static]);

    // ── State Change Wrappers ────────────────────────────────────────────────

    /**
     * When the mode changes, we need to clear all fields that are not relevant to the new mode.
     * For example, if switching to 'static', we should clear all auto and advanced fields. If switching to 'auto',
     * we should clear all advanced and static fields, etc. Additionally, we should clear any selected predefined option
     * since that may no longer be relevant after a mode change.
     */
    const onModeChange = React.useCallback((newMode: DateRangeFormMode) => {
        setMode(newMode);
        clearPredefined();
    }, [clearPredefined]);

    // =========================================================================
    // Auto-mode handlers
    // =========================================================================

    /**
     * When the type changes, we may need to enforce constraints on the count field. For example, if the new type is 'current',
     * the count should be cleared since it doesn't apply. If the new type is 'last' or 'next' and the current count is not a positive integer,
     * we should set it to 1 as a default valid value.
     */
    const changeCountByType = React.useCallback((newType: DateRangeType) => {
        if (newType === 'current') {
            setCount(undefined);
            onCountChangedProp?.(undefined);
        } else if (!count || count < 1) {
            setCount(1);
            onCountChangedProp?.(1);
        }
    }, [count, onCountChangedProp]);

    /**
     * When the type changes, we also need to enforce any constraints related to untilNow and fromNow.
     * For example, if the new type is 'current', we should set untilNow and fromNow to false (or undefined if disallowed).
     * If the new type is 'last', we should set untilNow to true and fromNow to undefined/false. If the new type is 'next',
     * we should set fromNow to true and untilNow to undefined/false.
     */
    const changeUntilOrFromNowByType = React.useCallback((newType: DateRangeType) => {
        if (newType === 'current') {
            const utv = !disallowUntilNow ? false : undefined;
            setUntilNow(utv);
            onUntilNowChangedProp?.(utv);
            const fnv = !disallowFromNow ? false : undefined;
            setFromNow(fnv);
            onFromNowChangedProp?.(fnv);
        } else if (newType === 'last') {
            setFromNow(undefined);
            onFromNowChangedProp?.(undefined);
            const utv = !disallowUntilNow ? true : undefined;
            setUntilNow(utv);
            onUntilNowChangedProp?.(utv);
        } else if (newType === 'next') {
            const fnv = !disallowFromNow ? true : undefined;
            setFromNow(fnv);
            onFromNowChangedProp?.(fnv);
            setUntilNow(undefined);
            onUntilNowChangedProp?.(undefined);
        }
    }, [disallowUntilNow, disallowFromNow, onUntilNowChangedProp, onFromNowChangedProp]);

    /**
     * When the type changes, we need to enforce any constraints related to the new type.
     * For example, if the new type is 'current', we should clear the count (since it doesn't apply) and
     * set untilNow/fromNow to false (or undefined if disallowed). If the new type is 'last', we should set untilNow to true and fromNow
     * to undefined/false. If the new type is 'next', we should set fromNow to true and untilNow to undefined/false.
     */
    const onTypeChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const v = e.target.value as DateRangeType;
        setType(v);
        clearPredefined();
        onTypeChangedProp?.(v);
        changeCountByType(v);
        changeUntilOrFromNowByType(v);
    }, [onTypeChangedProp, changeCountByType, changeUntilOrFromNowByType, clearPredefined]);

    /**
     * When the batch changes, we simply update the state and clear any predefined selection.
     * The batch options are not interdependent with other fields in the same way that the type is, so we don't need additional logic here.
     */
    const onBatchChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const v = e.target.value as DateRangeBatch;
        setBatch(v);
        clearPredefined();
        onBatchChangedProp?.(v);
    }, [onBatchChangedProp, clearPredefined]);

    /**
     * When the count changes, if the type is not 'current' and the new count is less than 1, we should set it to 1,
     * since it doesn't make sense to have a non-positive count for past/future ranges. If the type is 'current', the count should be cleared,
     * since it doesn't apply.
     */
    const onCountChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const v = parseInt(e.target.value);
        setCount(v);
        clearPredefined();
        onCountChangedProp?.(v);
    }, [onCountChangedProp, clearPredefined]);

    /**
     * When changing the "Until Now" checkbox, if the type is 'current', we need to ensure "From Now" is false/unchecked,
     * since it doesn't make sense to have a range that is both until and from now.
     */
    const onUntilNowChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.checked;
        setUntilNow(v);
        clearPredefined();
        onUntilNowChangedProp?.(v);
        if (type === 'current') {
            const fnv = !disallowFromNow ? false : undefined;
            setFromNow(fnv);
            onFromNowChangedProp?.(fnv);
        }
    }, [onUntilNowChangedProp, onFromNowChangedProp, disallowFromNow, type, clearPredefined]);

    /**
     * When the "From Now" checkbox is toggled, update the state and also enforce any constraints based on the selected type.
     * For example, if the type is 'current' and "From Now" is checked, we might want to uncheck "Until Now" to avoid conflicting states.
     */
    const onFromNowChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.checked;
        setFromNow(v);
        clearPredefined();
        onFromNowChangedProp?.(v);
        if (type === 'current') {
            const utv = !disallowUntilNow ? false : undefined;
            setUntilNow(utv);
            onUntilNowChangedProp?.(utv);
        }
    }, [onFromNowChangedProp, onUntilNowChangedProp, disallowUntilNow, type, clearPredefined]);

    // =========================================================================
    // Apply / Cancel
    // =========================================================================

    const isAutoValid = React.useMemo(() => !!type && !!batch && (type === 'current' || (!!count && count >= 1)), [type, batch, count]);

    const isAdvancedValid = React.useMemo(() => (
        (startFromNow || (!!startCount && startCount >= 1 && !!startBatch)) ||
        (endFromNow || (!!endCount && endCount >= 1 && !!endBatch))
    ), [startFromNow, startCount, startBatch, endFromNow, endCount, endBatch]);

    const isStaticValid = React.useMemo(() => !!startDate && !!endDate, [startDate, endDate]);

    const isApplyDisabled = React.useMemo(() => disabled || (
        mode === 'auto' ? !isAutoValid :
            mode === 'advanced' ? !isAdvancedValid :
                !isStaticValid
    ), [mode, isAutoValid, isAdvancedValid, isStaticValid, disabled]);

    const availableTypes = ALL_TYPES.filter(t => !disallowedTypes?.includes(t));
    const availableBatches = ALL_BATCHES.filter(b => !disallowedBatches?.includes(b));
    const defaultAdvancedType = availableTypes.includes('last') ? 'last' : 'next';

    /**
     * Clears the current selection and commits an undefined value, which signals the parent to clear any applied date range.
     */
    const onCleared = React.useCallback(() => {
        setType(undefined);
        setBatch(undefined);
        setCount(undefined);
        setUntilNow(undefined);
        setFromNow(undefined);
        setStartCount(undefined);
        setStartBatch(undefined);
        setStartType(undefined);
        setStartFromNow(false);
        setEndCount(undefined);
        setEndBatch(undefined);
        setEndType(undefined);
        setEndFromNow(false);
        setStartDate('');
        setEndDate('');
        clearPredefined();
        onCommitted?.(undefined)
    }, [clearPredefined, onCommitted]);

    /**
     * Constructs the value object based on the current mode and state, and calls onCommitted with it.
     */
    const commitChanges = React.useCallback(() => {
        let val: CommitDateRangeArg;
        switch (mode) {
            case 'advanced':
                val = {
                    mode,
                    advanced: {
                        startCount, startBatch, startType: startType ?? defaultAdvancedType, startFromNow,
                        endCount, endBatch, endType: endType ?? defaultAdvancedType, endFromNow
                    } satisfies AdvancedDateRangeValue,
                };
                break;
            case 'static':
                val = {mode, static: {startDate, endDate}};
                break;
            case 'auto':
            default:
                val = {mode, auto: {type, batch, count, untilNow, fromNow}};
                break;
        }
        val.predefinedId = predefinedId;
        onCommitted?.(val);
    }, [mode, type, batch, count, untilNow, fromNow, startCount, startBatch, startType, startFromNow, endCount, endBatch, endType, endFromNow, startDate, endDate, onCommitted, defaultAdvancedType, predefinedId]);

    // =========================================================================
    // Render helpers
    // =========================================================================

    /**
     * The sx style mixin for the condition's toggle button group.
     */
    const toggleButtonGroupMixin = useCallback((theme) => {
        return ({
            width: '100%',
            border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
            '& .MuiToggleButton-root': {
                backgroundColor: 'transparent',
                borderColor: 'transparent !important',
                color: theme.vars?.palette.text.primary,
                height: 30,
                width: 'calc(50% - 4px)',
                marginBlock: 0.5,
                marginInline: 0.5,
                padding: 1,
            },
            '& .MuiToggleButton-root:hover': {
                backgroundColor: theme.vars?.palette.background.tertiary,
                borderColor: `${theme.vars?.palette.background.tertiary} !important`,
                color: theme.palette.text.primary,
            },
            '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme.vars?.palette.background.tertiary,
                borderColor: `${theme.vars?.palette.background.tertiary} !important`,
                color: theme.vars?.palette.primary.main,
            },
            '& .MuiToggleButton-root.Mui-selected:hover': {
                color: theme.vars?.palette.primary.main,
                backgroundColor: theme.vars?.palette.background.tertiary,
                borderColor: `${theme.vars?.palette.background.tertiary} !important`,
            },
        })
    }, []);

    const groupedPredefinedOptions = React.useMemo(() => {
        const options = predefinedOptions ?? [];
        const predefinedGroups: Record<string, (PredefinedDateRange & { groupTitle: string })[]> = {};
        options.forEach(opt => {
            const group = opt.group ?? '';
            if (!predefinedGroups[group]) {
                predefinedGroups[group] = [];
            }
            predefinedGroups[group].push({
                ...opt,
                groupTitle: PredefinedDateRanges.allGroups[group]?.title ?? group,
            });
        });
        return Object.values(predefinedGroups).map(group => ({
            title: group[0]?.groupTitle ?? "",
            id: group[0]?.group ?? '',
            options: group,
        }));
    }, [predefinedOptions]);

    return (
        <Box sx={theme => ({
            paddingInline: 2,
            paddingBlockStart: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: `min(90vw, 450px)`,
            width: '100%',
            maxWidth: `min(90vw, 600px)`,
            ...UIUtils.invokeSx(sx, theme) as any,
        })}>
            {
                groupedPredefinedOptions.length > 0 &&
                <>
                    {
                        groupedPredefinedOptions.map(group => (
                            <Box key={group.id || "without_groups"} sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                <Typography variant="overline" color="text.secondary" sx={{mb: -1, fontWeight: 700}}>
                                    {group.title || "Predefined Dates"}
                                </Typography>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                    {group.options.map(opt => (
                                        <Button
                                            key={opt.id}
                                            variant={predefinedId === opt.id ? 'contained' : 'outlined'}
                                            size="small"
                                            color="secondary"
                                            onClick={() => handleSelectPredefined(opt)}
                                            disabled={disabled}
                                        >
                                            {opt.title}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        ))
                    }
                    <Typography variant="overline" color="text.secondary" sx={{mb: -1, fontWeight: 700}}>
                        Dynamic Dates
                    </Typography>
                </>
            }
            <ToggleButtonGroup
                orientation="horizontal"
                value={mode}
                exclusive
                onChange={(e, value) => {
                    if (value) {
                        onModeChange(value)
                    }
                }}
                sx={toggleButtonGroupMixin}
            >
                <ToggleButton value={'auto'}>
                    <Typography variant={'overline'} fontWeight={600}>
                        Auto
                    </Typography>
                </ToggleButton>
                <ToggleButton value={'advanced'}>
                    <Typography variant={'overline'} fontWeight={600}>
                        Advanced
                    </Typography>
                </ToggleButton>
                <ToggleButton value={'static'}>
                    <Typography variant={'overline'} fontWeight={600}>
                        Static
                    </Typography>
                </ToggleButton>
            </ToggleButtonGroup>
            {
                mode === 'auto' &&
                <AutoForm
                    type={type}
                    batch={batch}
                    count={count}
                    untilNow={untilNow}
                    fromNow={fromNow}
                    disabled={disabled}
                    availableTypes={availableTypes}
                    availableBatches={availableBatches}
                    disallowUntilNow={disallowUntilNow}
                    disallowFromNow={disallowFromNow}
                    onTypeChanged={onTypeChanged}
                    onBatchChanged={onBatchChanged}
                    onCountChanged={onCountChanged}
                    onUntilNowChanged={onUntilNowChanged}
                    onFromNowChanged={onFromNowChanged}
                />
            }
            {
                mode === 'advanced' &&
                <AdvancedForm
                    startCount={startCount}
                    startBatch={startBatch}
                    startType={startType ?? defaultAdvancedType}
                    startFromNow={startFromNow}
                    endCount={endCount}
                    endBatch={endBatch}
                    endType={endType ?? defaultAdvancedType}
                    endFromNow={endFromNow}
                    disabled={disabled}
                    availableBatches={availableBatches}
                    availableTypes={availableTypes}
                    onStartCountChange={setStartCount}
                    onStartBatchChange={setStartBatch}
                    onStartTypeChange={setStartType}
                    onStartFromNowChange={setStartFromNow}
                    onEndCountChange={setEndCount}
                    onEndBatchChange={setEndBatch}
                    onEndTypeChange={setEndType}
                    onEndFromNowChange={setEndFromNow}
                />
            }
            {
                mode === 'static' &&
                <StaticForm
                    startDate={startDateDayjs}
                    endDate={endDateDayjs}
                    inputMode={inputMode}
                    disabled={disabled}
                    availableTypes={availableTypes}
                    onStartDateChange={v => {
                        setStartDate(v?.format(inputMode === 'datetime-local' ? DateUtils.MUIFormat.dateTimePicker : DateUtils.MUIFormat.datePicker) ?? '');
                        clearPredefined();
                    }}
                    onEndDateChange={v => {
                        setEndDate(v?.format(inputMode === 'datetime-local' ? DateUtils.MUIFormat.dateTimePicker : DateUtils.MUIFormat.datePicker) ?? '');
                        clearPredefined();
                    }}
                />
            }

            {/* ── Actions ── */}
            {
                !hideActions &&
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, position: 'sticky', bottom: 0, backgroundColor: 'background.paper', paddingBlockEnd: 2, zIndex: 1}}>
                    <Divider orientation="horizontal" sx={{borderColor: 'divider', marginInline: -2}} flexItem/>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1}}>
                        <Button variant="text" color="primary" onClick={onCancelled} disabled={disabled}>
                            Cancel
                        </Button>
                        <Button variant="text" color="primary" onClick={onCleared} disabled={disabled}>
                            Clear
                        </Button>
                        <Button variant="outlined" color="primary" onClick={commitChanges} disabled={isApplyDisabled}>
                            Apply
                        </Button>
                    </Box>
                </Box>
            }
        </Box>
    );
};

export default DateRangeForm;

// =============================================================================
// Sub-forms
// =============================================================================

// ── Auto ─────────────────────────────────────────────────────────────────────

interface AutoFormProps {
    type?: DateRangeType;
    batch?: DateRangeBatch;
    count?: DateRangeCount;
    untilNow?: boolean;
    fromNow?: boolean;
    disabled?: boolean;
    availableTypes: DateRangeType[];
    availableBatches: DateRangeBatch[];
    disallowUntilNow?: boolean;
    disallowFromNow?: boolean;
    onTypeChanged: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBatchChanged: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onCountChanged: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onUntilNowChanged: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFromNowChanged: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const AutoForm: React.FC<AutoFormProps> = ({
                                               type, batch, count, untilNow, fromNow,
                                               disabled, availableTypes, availableBatches,
                                               disallowUntilNow, disallowFromNow,
                                               onTypeChanged, onBatchChanged, onCountChanged, onUntilNowChanged, onFromNowChanged,
                                           }) => (
    <Box sx={{display: 'inline-flex', gap: 1, width: '100%', alignItems: 'center', flexWrap: 'wrap', mt: 1}}>
        <TextField
            select size="small" disabled={disabled} label="Type"
            value={type ?? ''} onChange={onTypeChanged} sx={{minWidth: 100}}
        >
            {availableTypes.map(t => (
                <MenuItem key={t} value={t}>{DateRangeUtils.capitalizeFirstLetter(t)}</MenuItem>
            ))}
        </TextField>

        {type !== 'current' && (
            <TextField
                size="small" disabled={disabled} label="Count" type="number"
                value={count ?? ''} onChange={onCountChanged}
                inputProps={{min: 1}} sx={{width: 100}}
            />
        )}

        <TextField
            select size="small" disabled={disabled} label="Batch"
            value={batch ?? ''} onChange={onBatchChanged} sx={{minWidth: 100}}
        >
            {availableBatches.map(b => (
                <MenuItem key={b} value={b}>{DateRangeUtils.capitalizeFirstLetter(b)}</MenuItem>
            ))}
        </TextField>

        {!disallowUntilNow && (type === 'current' || type === 'last') && (
            <FormControlLabel
                label="Until Now" disabled={disabled} sx={{margin: 0}}
                control={
                    <Checkbox
                        disableRipple checked={!!untilNow} onChange={onUntilNowChanged}
                        icon={<CheckboxIcon/>} checkedIcon={<ActiveCheckboxIcon/>}
                    />
                }
            />
        )}

        {!disallowFromNow && (type === 'current' || type === 'next') && (
            <FormControlLabel
                label="From Now" disabled={disabled} sx={{margin: 0}}
                control={
                    <Checkbox
                        disableRipple checked={!!fromNow} onChange={onFromNowChanged}
                        icon={<CheckboxIcon/>} checkedIcon={<ActiveCheckboxIcon/>}
                    />
                }
            />
        )}
    </Box>
);

// ── Advanced ──────────────────────────────────────────────────────────────────

interface AdvancedFormProps {
    startCount?: number;
    startBatch?: DateRangeBatch;
    startType?: DateRangeType;
    startFromNow: boolean;
    endCount?: number;
    endBatch?: DateRangeBatch;
    endType?: DateRangeType;
    endFromNow: boolean;
    disabled?: boolean;
    availableBatches: DateRangeBatch[];
    availableTypes: DateRangeType[];
    onStartCountChange: (v?: number) => void;
    onStartBatchChange: (v?: DateRangeBatch) => void;
    onStartTypeChange: (v?: DateRangeType) => void;
    onStartFromNowChange: (v: boolean) => void;
    onEndCountChange: (v?: number) => void;
    onEndBatchChange: (v?: DateRangeBatch) => void;
    onEndTypeChange: (v?: DateRangeType) => void;
    onEndFromNowChange: (v: boolean) => void;
}

const AdvancedForm: React.FC<AdvancedFormProps> = ({
                                                       startCount, startBatch, startType, startFromNow,
                                                       endCount, endBatch, endType, endFromNow,
                                                       disabled, availableBatches, availableTypes,
                                                       onStartCountChange, onStartBatchChange, onStartTypeChange, onStartFromNowChange,
                                                       onEndCountChange, onEndBatchChange, onEndTypeChange, onEndFromNowChange,
                                                   }) => {
    // Filter availableTypes to just 'last' and 'next'
    const directionTypes = availableTypes.filter(t => t === 'last' || t === 'next');

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Configure independent start and end offsets from the current moment.
            </Typography>

            {/* Start row */}
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{mb: 0.5, display: 'block'}}>
                    Start
                </Typography>
                <Box sx={{display: 'inline-flex', gap: 1, alignItems: 'center', flexWrap: 'wrap'}}>
                    <FormControlLabel
                        label="Now" disabled={disabled} sx={{margin: 0}}
                        control={
                            <Checkbox
                                disableRipple checked={startFromNow}
                                onChange={e => onStartFromNowChange(e.target.checked)}
                                icon={<CheckboxIcon/>} checkedIcon={<ActiveCheckboxIcon/>}
                            />
                        }
                    />
                    {!startFromNow && (
                        <>
                            <TextField
                                size="small" disabled={disabled} label="Count" type="number"
                                value={startCount ?? ''} sx={{width: 90}}
                                onChange={e => onStartCountChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                inputProps={{min: 1}}
                            />
                            <TextField
                                select size="small" disabled={disabled} label="Batch"
                                value={startBatch ?? ''} sx={{minWidth: 100}}
                                onChange={e => onStartBatchChange(e.target.value as DateRangeBatch)}
                            >
                                {availableBatches.map(b => (
                                    <MenuItem key={b} value={b}>{DateRangeUtils.capitalizeFirstLetter(b)}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select size="small" disabled={disabled} label="Direction"
                                value={startType ?? 'last'} sx={{minWidth: 100}}
                                onChange={e => onStartTypeChange(e.target.value as DateRangeType)}
                            >
                                {directionTypes.map(t => (
                                    <MenuItem key={t} value={t}>{t === 'last' ? 'ago' : 'ahead'}</MenuItem>
                                ))}
                            </TextField>
                        </>
                    )}
                </Box>
            </Box>

            {/* End row */}
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{mb: 0.5, display: 'block'}}>
                    End
                </Typography>
                <Box sx={{display: 'inline-flex', gap: 1, alignItems: 'center', flexWrap: 'wrap'}}>
                    <FormControlLabel
                        label="Now" disabled={disabled} sx={{margin: 0}}
                        control={
                            <Checkbox
                                disableRipple checked={endFromNow}
                                onChange={e => onEndFromNowChange(e.target.checked)}
                                icon={<CheckboxIcon/>} checkedIcon={<ActiveCheckboxIcon/>}
                            />
                        }
                    />
                    {!endFromNow && (
                        <>
                            <TextField
                                size="small" disabled={disabled} label="Count" type="number"
                                value={endCount ?? ''} sx={{width: 90}}
                                onChange={e => onEndCountChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                inputProps={{min: 1}}
                            />
                            <TextField
                                select size="small" disabled={disabled} label="Batch"
                                value={endBatch ?? ''} sx={{minWidth: 100}}
                                onChange={e => onEndBatchChange(e.target.value as DateRangeBatch)}
                            >
                                {availableBatches.map(b => (
                                    <MenuItem key={b} value={b}>{DateRangeUtils.capitalizeFirstLetter(b)}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select size="small" disabled={disabled} label="Direction"
                                value={endType ?? 'last'} sx={{minWidth: 100}}
                                onChange={e => onEndTypeChange(e.target.value as DateRangeType)}
                            >
                                {directionTypes.map(t => (
                                    <MenuItem key={t} value={t}>{t === 'last' ? 'ago' : 'ahead'}</MenuItem>
                                ))}
                            </TextField>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ── Static ────────────────────────────────────────────────────────────────────

interface StaticFormProps {
    startDate: DateUtilsTypes.Instance | null;
    endDate: DateUtilsTypes.Instance | null;
    inputMode: DateRangeInputMode;
    disabled?: boolean;
    availableTypes: DateRangeType[];
    onStartDateChange: (v: DateUtilsTypes.Instance | null) => void;
    onEndDateChange: (v: DateUtilsTypes.Instance | null) => void;
}

const StaticForm: React.FC<StaticFormProps> = ({
                                                   startDate, endDate, inputMode, disabled, availableTypes,
                                                   onStartDateChange, onEndDateChange,
                                               }) => {
    // Compute min/max constraints based on available direction types
    const now = DateUtils.localNow();
    const maxDate = availableTypes.includes('next') ? undefined : now;
    const minDate = availableTypes.includes('last') ? undefined : now;

    const slotProps = (label: string) => ({
        textField: {size: 'small' as const, fullWidth: true, disabled, label},
    });

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            <DateInput
                allowDate={inputMode === 'date' || inputMode === 'datetime-local'}
                allowTime={inputMode === 'datetime-local'}
                value={startDate}
                setValue={onStartDateChange}
                disabled={disabled}
                slotProps={slotProps('Start Date')}
                maxDate={maxDate}
                minDate={minDate}
            />
            <DateInput
                allowDate={inputMode === 'date' || inputMode === 'datetime-local'}
                allowTime={inputMode === 'datetime-local'}
                value={endDate}
                setValue={onEndDateChange}
                disabled={disabled}
                slotProps={slotProps('End Date')}
                maxDate={maxDate}
                minDate={minDate}
            />
        </Box>
    );
};

interface DateInputProps {
    value: DateUtilsTypes.Instance | null;
    setValue: (v: DateUtilsTypes.Instance | null) => void;
    allowTime?: boolean;
    allowDate?: boolean;
    disabled?: boolean;
    slotProps?: DatePickerSlotsComponentsProps<DateUtilsTypes.Instance> | DateTimePickerSlotsComponentsProps<DateUtilsTypes.Instance> | TimePickerSlotsComponentsProps<DateUtilsTypes.Instance>;
    maxDate?: DateUtilsTypes.Instance;
    minDate?: DateUtilsTypes.Instance;
    otherProps?: Omit<DatePickerProps<DateUtilsTypes.Instance>, 'onChange' | 'value' | 'components' | "componentsProps">
        | Omit<DateTimePickerProps<DateUtilsTypes.Instance>, 'onChange' | 'value' | 'components' | "componentsProps">
        | Omit<TimePickerProps<DateUtilsTypes.Instance>, 'onChange' | 'value' | 'components' | "componentsProps">
}

const valueSetter = (value: DateUtilsTypes.Instance | null) => (value ? DateUtils.localToUTC(value) : null);

export const DateInput: React.FC<DateInputProps> = ({value: valueProps, setValue: setValueProps, allowDate, allowTime, disabled, slotProps, maxDate, minDate, otherProps}) => {
    const [value, setValue] = useState<DateUtilsTypes.Instance | null>(null);

    /**
     * With each change in the [rule.value] prop:
     * - updates the local state.
     */
    useEffect(() => {
        setValue(valueSetter(valueProps))
    }, [valueProps]);

    /**
     * Commits the rule's value in the reducer for string types.
     */
    const commitDateValue = useCallback((value: DateUtilsTypes.Instance | null) => {
        if (value?.isValid() || !value) {
            setValue(value);
            setValueProps(value);
        }
    }, [setValueProps]);

    if (allowDate && allowTime) {
        return (
            <DateTimePicker<DateUtilsTypes.Instance>
                {...otherProps as Omit<DateTimePickerProps<DateUtilsTypes.Instance>, 'onChange' | 'value' | 'components' | "componentsProps">}
                disabled={disabled}
                value={value}
                onChange={commitDateValue}
                slotProps={slotProps as DateTimePickerSlotsComponentsProps<DateUtilsTypes.Instance>}
                maxDate={maxDate}
                minDate={minDate}
            />
        )
    }
    if (allowDate) {
        return (
            <DatePicker<DateUtilsTypes.Instance>
                {...otherProps as Omit<DatePickerProps<DateUtilsTypes.Instance>, 'onChange' | 'value' | 'components' | "componentsProps">}
                disabled={disabled}
                value={value}
                onChange={commitDateValue}
                slotProps={slotProps as DatePickerSlotsComponentsProps<DateUtilsTypes.Instance>}
                maxDate={maxDate}
                minDate={minDate}
            />
        )
    }
    if (allowTime) {
        return (
            <TimePicker<DateUtilsTypes.Instance>
                {...otherProps as Omit<TimePickerProps<DateUtilsTypes.Instance>, 'onChange' | 'value' | 'components' | "componentsProps">}
                label={'Time Value'}
                disabled={disabled}
                value={value}
                onChange={commitDateValue}
                slotProps={slotProps as TimePickerSlotsComponentsProps<DateUtilsTypes.Instance>}
                maxTime={maxDate}
                minTime={minDate}
            />
        )
    }

    throw new Error("Invalid configuration: at least one of allowDate or allowTime must be true");
}
